import * as React from 'react';
import { get, isEmpty, isEqual } from 'lodash';
import * as uuid from 'uuid/v4';
import { Col, Grid, Row } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { compose, lifecycle, withHandlers, withProps, withStateHandlers } from 'recompose';
import { change, Field, reduxForm } from 'redux-form';

import Button from '@inqool/uas-web-core/es/components/controls/Button';

import { buildFullAddress } from '@utils/utils';
import { addressAutoCompleteEnum, addressInputEnum } from '@components/form/Address/_addressEnum';
import { ModalButton } from '@components/modalButton/ModalButton';
import AutoCompleteToggle from '@components/form/AutoCompleteToggle/AutoCompleteToggle';
import RemoteAutoComplete, { RemoteAutoCompleteField } from '@components/form/RemoteAutoComplete/RemoteAutoComplete';
import Input, { InputField } from '@components/form/Input/Input';
import * as T from '@components/form/Address/_addressTypes';
import { getAddressPartItems, getCoords } from '@components/form/Address/_addressActions';
import GPSButton from '@components/form/GPSBtn/GPSButton';
import { withNotifications } from '@components/hoc/withNotifications';

const parseCoords = coords => (coords && coords.length === 2 ? coords : null);

const MAX_LEVEL = addressAutoCompleteEnum.length;

const buttonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline'
};

const buildAddressBody = formData => {
  const { id, coords } = formData;
  return {
    ...formData,
    id: id ? id : uuid(),
    coords: parseCoords(coords)
  };
};

const valuesToCheck = [...addressAutoCompleteEnum, ...addressInputEnum, { name: 'coords' }].map(({ name }) => name);

const mapStateToProps = (state, { oldAddressValues }) => {
  if (isEmpty(oldAddressValues)) {
    return { showWarning: false };
  }

  const newAddressValues = get(state, 'form.module-address.values');
  const showWarning = valuesToCheck.some(value => !isEqual(get(oldAddressValues, value), get(newAddressValues, value)));
  return { showWarning };
};

const enhance = compose<any, T.OutterProps>(
  withNotifications({ getCoords }),
  connect<any>(
    undefined,
    dispatch => ({
      changeFieldValue: (label, value) => dispatch(change('module-address', label, value))
    })
  ),
  withProps(({ input }) => ({ initialValues: get(input, 'value') || {}, submitAddress: get(input, 'onChange') })),
  reduxForm({ form: 'module-address' }),
  withStateHandlers<any, any>(
    ({ initialValues }) => ({
      oldAddressValues: initialValues,
      toggleLevel: MAX_LEVEL,
      disabledLebel: MAX_LEVEL,
      listOfItems: []
    }),
    {
      handleOldAddressValues: s => oldAddressValues => ({ ...s, oldAddressValues }),
      handleToggleLevel: s => toggleLevel => ({ ...s, toggleLevel }),
      handleDisabledLevel: s => disabledLevel => ({ ...s, disabledLevel }),
      handleListOfItems: s => listOfItems => ({ ...s, listOfItems })
    }
  ),
  withHandlers({
    cascadeDelete: ({ changeFieldValue }) => (index: number, defaultValues?: object) => {
      for (let i = index; i < addressAutoCompleteEnum.length; ++i) {
        const addressPart = get(addressAutoCompleteEnum[i], 'name');
        changeFieldValue(addressPart, get(defaultValues, addressPart, ''));
        changeFieldValue(`${addressPart}Id`, get(defaultValues, `${addressPart}Id`, ''));
      }

      addressInputEnum.forEach(addressInput => changeFieldValue(addressInput.name, ''));
    }
  }),
  withHandlers({
    onSubmit: ({ submitAddress, closeItem }) => formData => {
      const fullAddress = buildFullAddress(formData);
      const body = buildAddressBody({ ...formData, fullAddress });
      submitAddress(body);
      closeItem();
    },
    getCoordsFromAddress: ({
      getCoordsNotify,
      changeFieldValue,
      handleLoader,
      handleOldAddressValues
    }) => async formData => {
      handleLoader(true);
      const { coords } = formData;
      const formDataParsed = { ...formData, coords: parseCoords(coords) };
      const gpsCoords = await getCoordsNotify(formDataParsed);
      handleOldAddressValues({ ...formData, gpsCoords });
      changeFieldValue('coords', gpsCoords);
      handleLoader(false);
    },
    resetAddress: ({ cascadeDelete }) => () => {
      cascadeDelete(0);
    },
    onToggle: ({ handleToggleLevel, handleDisabledLevel, cascadeDelete }) => index => (_, value) => {
      cascadeDelete(index);
      handleToggleLevel(value ? index : MAX_LEVEL);
      // because municipalityPart should not disable street
      handleDisabledLevel(value || index > 3 ? MAX_LEVEL : index);
    },
    onDelete: ({ cascadeDelete, handleDisabledLevel }) => index => () => {
      cascadeDelete(index);
      handleDisabledLevel(index);
    },
    onChange: ({
      listOfItems,
      handleListOfItems,
      handleDisabledLevel,
      changeFieldValue,
      cascadeDelete
    }) => index => async (_, id) => {
      const addressPart = listOfItems[index].find(o => get(o, 'id') === id);
      const field = addressAutoCompleteEnum[index].name;

      changeFieldValue(field, get(addressPart, 'name'));

      if (field !== 'street' && field !== 'municipalityPart') {
        cascadeDelete(index + 1);

        const filter = { field: `${field}.id`, value: id };
        const anotherField = addressAutoCompleteEnum[index + 1].url;
        const newListOfItems = listOfItems;
        const newItems = await getAddressPartItems(anotherField, filter);
        newListOfItems[index + 1] = newItems;

        if (field === 'municipality') {
          const streetItems = await getAddressPartItems(addressAutoCompleteEnum[index + 2].url, filter);
          newListOfItems[index + 2] = streetItems;
          handleDisabledLevel(index + 2);
        } else {
          handleDisabledLevel(index + 1);
        }
        handleListOfItems(newListOfItems);
      }
    },
    handleAddressfill: ({ changeFieldValue }) => addressValues => {
      addressInputEnum.forEach(
        addressInput =>
          addressValues[addressInput.name] && changeFieldValue(addressInput.name, addressValues[addressInput.name])
      );
      if (addressValues.coords) {
        changeFieldValue('coords', addressValues.coords);
      }
    },
    handleAutofill: ({
      handleLoader,
      handleToggleLevel,
      handleDisabledLevel,
      handleListOfItems
    }) => async initialValues => {
      handleLoader(true);
      const promises: Array<Promise<any>> = [];
      let toggleLevel = MAX_LEVEL;

      promises[0] = getAddressPartItems('countries');

      for (let i = 0; i < MAX_LEVEL; ++i) {
        const field = addressAutoCompleteEnum[i].name;

        if (field !== 'street' && field !== 'municipalityPart' && initialValues[`${field}Id`]) {
          const filter = { field: `${field}.id`, value: initialValues[`${field}Id`] };
          promises[i + 1] = getAddressPartItems(addressAutoCompleteEnum[i + 1].url, filter);

          if (field === 'municipality') {
            promises[i + 2] = getAddressPartItems(addressAutoCompleteEnum[i + 2].url, filter);
            handleToggleLevel(MAX_LEVEL);
            handleDisabledLevel(MAX_LEVEL);
          }
        } else {
          if (!!initialValues[`${field}`] && !initialValues[`${field}Id`]) {
            toggleLevel = i < toggleLevel ? i : toggleLevel;
            handleToggleLevel(toggleLevel);
            handleDisabledLevel(i);
          }
        }
      }
      const listOfItems = await Promise.all(promises);
      handleListOfItems(listOfItems);

      handleLoader(false);
    }
  }),
  withHandlers({
    handleCustomPlace: ({ handleAutofill, handleAddressfill, cascadeDelete }) => ({ address }) => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!address) {
        cascadeDelete(0, address);
        handleAutofill(address);
        handleAddressfill(address);
      }
    }
  }),
  lifecycle<any, any>({
    async componentDidMount() {
      const { initialValues, defaultPlace, handleAutofill, handleCustomPlace, changeFieldValue } = this.props;

      if (Object.keys(initialValues).length) {
        handleAutofill(initialValues);
        return;
      }

      if (defaultPlace) {
        handleCustomPlace(defaultPlace);
        changeFieldValue('place', defaultPlace);
        return;
      }

      handleAutofill({});
    }
  }),
  connect<any>(mapStateToProps)
);

const AddressModal: React.SFC<any> = ({
  closeItem,
  resetAddress,
  handleSubmit,
  listOfItems,
  toggleLevel,
  onToggle,
  onChange,
  onDelete,
  handleCustomPlace,
  handleLoader,
  onSubmit,
  getCoordsFromAddress,
  showWarning
}) => (
  <div style={{ margin: '15px' }}>
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid fluid={true}>
        <Row>
          <Col md={6}>
            <h4>Předvyplnit z vlastních míst:</h4>
          </Col>
          <Col md={6}>
            <RemoteAutoCompleteField
              component={RemoteAutoComplete}
              name="place"
              url="/metropolis/api/address/place"
              fullWidth={true}
              labelFunction={o => get(o, 'name')}
              valueFunction={o => o}
              onChange={handleCustomPlace}
            />
          </Col>
          {addressAutoCompleteEnum.map(({ name, label }, index) => (
            <Col md={6} key={name}>
              <AutoCompleteToggle
                label={label}
                inputName={name}
                autoCompleteName={`${name}Id`}
                items={get(listOfItems, index, [])}
                toggled={index >= toggleLevel}
                onToggle={onToggle(index)}
                onChange={onChange(index)}
                onDelete={onDelete(index)}
                fullWidth={true}
                sorting={false}
              />
            </Col>
          ))}
          {addressInputEnum.map(({ name, label }) => (
            <Col md={4} key={name}>
              <InputField component={Input} label={label} name={name} />
            </Col>
          ))}

          <Col md={12}>
            <h4>GPS Souřadnice</h4>
          </Col>
          <Col md={4}>
            <InputField component={Input} type="number" label="Zeměpisná šířka" name="coords[0]" />
          </Col>
          <Col md={4}>
            <InputField component={Input} type="number" label="Zeměpisná délka" name="coords[1]" />
          </Col>
          <Col md={4} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <Button label="Získat GPS" onClick={handleSubmit(getCoordsFromAddress)} style={{ width: '100%' }} />
          </Col>
          <Col
            md={12}
            style={{
              visibility: showWarning ? 'visible' : 'hidden',
              color: 'red',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <span>Při změně údajů se nemusí gps a adresa shodovat</span>
          </Col>
        </Row>
      </Grid>
      <div className="buttons" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <Button type="submit" label="Uložit a zavřít" primary={true} />
          <Button label="Zavřít" onClick={closeItem} />
          <Button label="Resetovat" onClick={resetAddress} primary={true} buttonStyle={{ backgroundColor: 'red' }} />
        </div>
        <GPSButton
          label="Předvyplnit z GPS"
          handleCustomPlace={handleCustomPlace}
          handleLoader={handleLoader}
          style={{ margin: '16px' }}
        />
      </div>
    </form>
  </div>
);

const Address: React.SFC<any> = ({ label, disabled, columnStyle, MiddleComponent, ...props }) => {
  const {
    input: { name }
  } = props;

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: columnStyle ? 'column' : 'row'
      }}
    >
      <InputField component={Input} label={label} name={`${name}.fullAddress`} disabled={true} />
      {MiddleComponent && <MiddleComponent {...props} />}
      <div style={buttonStyle}>
        <ModalButton
          label={'Vybrat adresu'}
          disabled={disabled}
          style={{
            margin: 10,
            whiteSpace: 'nowrap',
            minWidth: 'none'
          }}
          dialogStyles={{
            style: { paddingTop: '5vh' },
            contentStyle: {
              paddingTop: 10,
              maxWidth: '100vw',
              transform: 'none'
            },
            bodyStyle: { padding: '0', overflow: 'auto' }
          }}
          ModalComponent={enhance(AddressModal)}
          {...props}
        />
      </div>
    </div>
  );
};

export default Address;

export class AddressField extends Field<T.OutterCustomProps> {}



// WEBPACK FOOTER //
// ./src/components/form/Address/Address.tsx